import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePortalRole } from '../../contexts/PortalRoleContext';
import styled from 'styled-components';
import { getMyProfile, updateMyProfile, uploadAvatar } from '../../api/userProfileApi';
import AvatarUpload from '../../components/Portal/AvatarUpload';

const Container = styled.div`
  max-width: 900px;
  margin: 0 auto;
  padding: 80px 24px 24px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 32px;

  h1 {
    color: #111827;
    font-size: 28px;
    font-weight: bold;
    margin: 0;
  }
`;

const ProfileGrid = styled.div`
  display: grid;
  grid-template-columns: 280px 1fr;
  gap: 24px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ProfileCard = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  padding: 24px;
  text-align: center;
  height: fit-content;
`;

const ProfileName = styled.h2`
  color: #111827;
  font-size: 22px;
  font-weight: bold;
  margin: 16px 0 8px 0;
`;

const ProfileEmail = styled.p`
  color: #6b7280;
  font-size: 14px;
  margin: 0 0 4px 0;
`;

const ProfileBadge = styled.span`
  display: inline-block;
  background: #8b5cf6;
  color: white;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  margin-top: 12px;
`;

const ContentArea = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const Card = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  overflow: hidden;
`;

const CardHeader = styled.div`
  padding: 20px 24px;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;

  h3 {
    color: #111827;
    font-size: 18px;
    font-weight: 600;
    margin: 0;
  }
`;

const CardContent = styled.div`
  padding: 24px;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  color: #374151;
  font-size: 14px;
  font-weight: 500;
`;

const Input = styled.input`
  padding: 10px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: #8b5cf6;
    box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.15);
  }

  &:disabled {
    background: #f9fafb;
    color: #9ca3af;
    cursor: not-allowed;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  padding-top: 16px;
  border-top: 1px solid #e5e7eb;
  margin-top: 20px;
`;

const Button = styled.button`
  padding: 10px 20px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  border: 1px solid transparent;

  &.primary {
    background: #8b5cf6;
    color: white;

    &:hover {
      background: #7c3aed;
    }

    &:disabled {
      background: #c4b5fd;
      cursor: not-allowed;
    }
  }

  &.secondary {
    background: white;
    color: #6b7280;
    border-color: #d1d5db;

    &:hover {
      background: #f9fafb;
    }
  }
`;

const Alert = styled.div`
  padding: 12px 16px;
  border-radius: 6px;
  margin-bottom: 20px;
  font-size: 14px;

  &.success {
    background: #f0fdf4;
    color: #047857;
    border: 1px solid #86efac;
  }

  &.error {
    background: #fef2f2;
    color: #dc2626;
    border: 1px solid #fecaca;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  color: #6b7280;
  font-size: 16px;
`;

const AvatarContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 16px;
`;

const ParentProfile = () => {
  const navigate = useNavigate();
  const { currentRole, hasRole } = usePortalRole();

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    birthday: '',
    address: '',
    avatarUrl: ''
  });

  // Load profile on mount
  useEffect(() => {
    const loadProfile = async () => {
      try {
        setIsLoading(true);
        setError('');
        const data = await getMyProfile();

        setFormData({
          name: data.name || '',
          email: data.email || '',
          phone: data.phone || '',
          birthday: data.birthday || '',
          address: data.address || '',
          avatarUrl: data.avatarUrl || ''
        });
      } catch (err) {
        console.error('Error loading profile:', err);
        setError('Failed to load profile. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    loadProfile();
  }, []);

  const handleSave = async () => {
    try {
      setIsSaving(true);
      setError('');
      await updateMyProfile({
        name: formData.name,
        phone: formData.phone,
        birthday: formData.birthday,
        address: formData.address
      });
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
    } catch (err) {
      console.error('Error saving profile:', err);
      setError('Failed to save profile. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleAvatarUpload = (newAvatarUrl) => {
    setFormData({ ...formData, avatarUrl: newAvatarUrl });
  };

  /**
   * Keep out anyone this page is not for.
   *
   * Checked against every role the user holds, not just their highest one. `currentRole` is the
   * most senior role a person has, so a teacher or manager whose own child attends resolved to
   * that senior role and was bounced off their own parent profile — while the navbar went on
   * showing them the link, because that is drawn from the full set. Same fix as TeacherProfile.
   */
  useEffect(() => {
    if (!hasRole('parent') && currentRole !== 'director' && currentRole !== 'system_admin') {
      navigate('/portal');
    }
  }, [hasRole, currentRole, navigate]);

  if (isLoading) {
    return (
      <Container>
        <LoadingContainer>Loading profile...</LoadingContainer>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <h1>My Profile</h1>
      </Header>

      {error && <Alert className="error">{error}</Alert>}

      <ProfileGrid>
        {/* Left Sidebar - Profile Summary */}
        <ProfileCard>
          <AvatarContainer>
            <AvatarUpload
              avatarUrl={formData.avatarUrl}
              name={formData.name}
              size={100}
              editable={true}
              onUpload={handleAvatarUpload}
              uploadFunction={uploadAvatar}
            />
          </AvatarContainer>
          <ProfileName>{formData.name || 'Parent'}</ProfileName>
          <ProfileEmail>{formData.email}</ProfileEmail>
          <ProfileBadge>Parent</ProfileBadge>
        </ProfileCard>

        {/* Right Side - Edit Form */}
        <ContentArea>
          <Card>
            <CardHeader>
              <h3>Personal Information</h3>
            </CardHeader>
            <CardContent>
              {showSuccess && (
                <Alert className="success">
                  Profile updated successfully!
                </Alert>
              )}

              <FormGrid>
                <FormGroup>
                  <Label>Full Name</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Your full name"
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    disabled
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Phone</Label>
                  <Input
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="e.g., 0412 345 678"
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Date of Birth</Label>
                  <Input
                    type="date"
                    value={formData.birthday}
                    onChange={(e) => setFormData({ ...formData, birthday: e.target.value })}
                  />
                </FormGroup>

                <FormGroup style={{ gridColumn: '1 / -1' }}>
                  <Label>Address</Label>
                  <Input
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    placeholder="Your address"
                  />
                </FormGroup>
              </FormGrid>

              <ButtonGroup>
                <Button className="secondary" onClick={() => window.location.reload()}>
                  Cancel
                </Button>
                <Button className="primary" onClick={handleSave} disabled={isSaving}>
                  {isSaving ? 'Saving...' : 'Save Changes'}
                </Button>
              </ButtonGroup>
            </CardContent>
          </Card>
        </ContentArea>
      </ProfileGrid>
    </Container>
  );
};

export default ParentProfile;
